import { Spacing } from '@persona-id/design-tokens';
import { groupBy } from 'lodash/fp';
import { useState } from 'react';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import Permissioned from 'dashboard/components/Permissioned';
import IgnoreHitsModal from 'dashboard/components/ReportResult/IgnoreHitsModal';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import WatchlistAccordions from 'dashboard/components/ReportResult/WatchlistAccordions';
import {
  BusinessWatchlistMatch,
  BusinessWatchlistType,
  ReportBusinessWatchlist,
  translateBusinessWatchlistType,
} from 'dashboard/models/report/business-watchlist';
import { translateWatchlistMatchType } from 'dashboard/models/report/watchlist';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { FontWeight } from 'design-system/variables/text';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const CATEGORY_ORDER = [
  BusinessWatchlistType.Sanction,
  BusinessWatchlistType.FitnessProbity,
  BusinessWatchlistType.Warning,
];

interface BusinessWatchlistProps {
  report: ReportBusinessWatchlist;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

const TitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const TabText = styled.div`
  font-weight: ${FontWeight.Medium};
  & > span {
    margin-right: ${Spacing[2]};
  }
`;

const BusinessWatchlist = ({
  report,
  isRedacted,
  multiCardFormat = false,
}: BusinessWatchlistProps) => {
  const [isActiveTabSelected, setIsActiveTabSelected] = useState(true);
  const [ignoreHitsModal, { enable: showIgnoreHitsModal, disable: hideIgnoreHitsModal }] =
    useToggle(false);

  if (isRedacted) {
    return <></>;
  }

  const {
    'matched-lists': matchedLists = [],
    'searched-categories': searchedCategories = [],
    'ignore-list': ignoreList = [],
  } = report.attributes;

  const groupedMatches = isActiveTabSelected
    ? groupBy('list-type', matchedLists)
    : groupBy(
        'list-type',
        ignoreList.map((entry) => entry.match),
      );
  const listsToDisplay = new Set([...searchedCategories, ...Object.keys(groupedMatches)]);

  const content = (
    <WatchlistAccordions<BusinessWatchlistMatch, BusinessWatchlistType>
      groupedMatches={groupedMatches}
      listOrder={CATEGORY_ORDER.filter((category) => listsToDisplay.has(category))}
      title={translateBusinessWatchlistType}
      subtitle={(match: BusinessWatchlistMatch) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return match.entities.map((entity, index) => {
          return (
            <div key={index}>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              {[translateWatchlistMatchType(entity['alias-type']), entity.name].join(' • ')}
            </div>
          );
        });
      }}
    />
  );

  return (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      <>
        <TitleContainer>
          <Card.Title>Results</Card.Title>
          <Permissioned permission={Permission.ReportIgnoreHitsWrite}>
            <ContextMenu
              variant='inverse'
              items={[
                {
                  id: 'reports-ignore-hits-modal__dismiss-hits-button',
                  onClick: showIgnoreHitsModal,
                  hidden: false,
                  label: 'Dismiss matches',
                },
              ]}
              renderItem={(item) => (
                <ContextMenu.Item key={item.id} id={item.id} onClick={item.onClick}>
                  {item.label}
                </ContextMenu.Item>
              )}
            />
            <IgnoreHitsModal
              report={report}
              ignoreHitsModalOpen={ignoreHitsModal}
              hideIgnoreHitsModal={hideIgnoreHitsModal}
            />
          </Permissioned>
        </TitleContainer>

        {ignoreList.length > 0 && (
          <>
            <TabBar>
              <Tab
                id='report-watchlist__ignored-tab-active'
                selected={isActiveTabSelected}
                onClick={() => {
                  setIsActiveTabSelected(true);
                }}
              >
                <TabText>
                  <span>Active</span>
                  <Sticker>{matchedLists.length}</Sticker>
                </TabText>
              </Tab>
              <Tab
                id='report-watchlist__ignored-tab-ignored'
                selected={!isActiveTabSelected}
                onClick={() => {
                  setIsActiveTabSelected(false);
                }}
              >
                <TabText>
                  <span>Dismissed</span>
                  <Sticker>{ignoreList.length}</Sticker>
                </TabText>
              </Tab>
            </TabBar>
            <Spacer height={2} />
          </>
        )}
        {content}
      </>
    </ReportResultLayout>
  );
};

export default BusinessWatchlist;
